import React, { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  query,
  where,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import Modal from "react-modal";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import usePlanFeatures from "../hooks/usePlanFeatures";
import {
  Search, PlusCircle, Edit2, Trash2, FileText, Tag, X, Save,
  DollarSign, Package, Plus, Check, Clock, Layers, AlertTriangle,
} from 'lucide-react';
import CustomDeleteDialog from '../components/CustomDeleteDialog';
import { StockBadge, StockAdjustModal, useStockStore } from '../features/stock';
import { normalizeTr } from '../utils/textUtils';

const IS_SAAS = process.env.REACT_APP_IS_SAAS === 'true';

Modal.setAppElement("#root");

// ── Paket Kartı ──────────────────────────────────────────────────────────────
function PackageCard({ pkg, onEdit, onToggle }) {
  const isActive = pkg.status === 'active';
  const totalSessions = (pkg.services || []).reduce((s, svc) => s + (svc.sessions || 0), 0);

  return (
    <div className={`bg-white rounded-xl border p-5 transition-all ${isActive ? 'border-gray-200' : 'border-gray-100 opacity-60'}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-base font-semibold text-gray-900 truncate">{pkg.name}</span>
            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
              {isActive ? 'Aktif' : 'Pasif'}
            </span>
          </div>
          {pkg.description && <p className="text-xs text-gray-500 mb-2">{pkg.description}</p>}
          <div className="flex flex-wrap gap-1.5 mb-2">
            {(pkg.services || []).map(svc => (
              <span key={svc.serviceId} className="text-xs bg-purple-50 text-purple-700 px-2 py-0.5 rounded-full">
                {svc.serviceName} × {svc.sessions}
              </span>
            ))}
          </div>
          <div className="flex items-center gap-3 text-xs text-gray-500">
            <span className="font-semibold text-gray-800">{pkg.price?.toLocaleString('tr-TR')} TL</span>
            <span>·</span>
            <span>{totalSessions} seans</span>
            <span>·</span>
            <span>{pkg.validityDays > 0 ? `${pkg.validityDays} gün geçerli` : 'Süresiz'}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={onEdit}
            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            title="Düzenle"
          >
            <Edit2 className="h-4 w-4" />
          </button>
          <button
            onClick={onToggle}
            className={`p-1.5 rounded-lg transition-colors ${
              isActive
                ? 'text-gray-400 hover:text-red-500 hover:bg-red-50'
                : 'text-gray-400 hover:text-green-600 hover:bg-green-50'
            }`}
            title={isActive ? 'Pasife Al' : 'Aktive Et'}
          >
            {isActive ? <Trash2 className="h-4 w-4" /> : <Check className="h-4 w-4" />}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Ana Bileşen ───────────────────────────────────────────────────────────────
function Services() {
  const navigate = useNavigate(); // eslint-disable-line no-unused-vars
  const { user } = useAuth();
  const { t } = useTranslation('services');
  const { isFeatureAllowed } = usePlanFeatures();
  const isStockAllowed    = isFeatureAllowed('stock');
  const isPackagesAllowed = isFeatureAllowed('packages');

  // ── Services state ──
  const [services, setServices] = useState([]);
  const [filteredServices, setFilteredServices] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  // ── Packages state ──
  const [packages, setPackages] = useState([]);

  // ── Modal state ──
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingCollection, setEditingCollection] = useState('services'); // 'services' | 'packages'
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [saving, setSaving] = useState(false);

  const emptyForm = () => ({
    name: '',
    price: '',
    type: 'İşlem',
    status: 'active',
    description: '',
    // Package-specific
    packageServices: [],
    validityDays: 0,
    // Product-specific (stok)
    trackStock: true,
    stockQty: 0,
    minStockQty: 5,
    unit: 'adet',
  });
  const [serviceForm, setServiceForm] = useState(emptyForm());
  const [selectedPkgServiceId, setSelectedPkgServiceId] = useState('');

  // ── Fetch ────────────────────────────────────────────────────────────────────
  const fetchServices = async () => {
    try {
      const snap = await getDocs(query(
        collection(db, "services"),
        where("businessId", "==", user?.businessId),
        where("status", "==", "active"),
      ));
      const data = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => a.name.localeCompare(b.name, 'tr', { sensitivity: 'base' }));
      setServices(data);
      setFilteredServices(data);
    } catch (err) {
      console.error("Veri çekme hatası:", err);
    }
  };

  const fetchPackages = async () => {
    if (!user?.businessId) return;
    try {
      const snap = await getDocs(query(
        collection(db, 'packages'),
        where('businessId', '==', user.businessId),
      ));
      setPackages(
        snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => a.name.localeCompare(b.name, 'tr', { sensitivity: 'base' })),
      );
    } catch (err) {
      console.error("Paket yükleme hatası:", err);
    }
  };

  useEffect(() => {
    fetchServices();
    fetchPackages();
  }, [user?.businessId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Search ───────────────────────────────────────────────────────────────────
  const handleSearch = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    // Türkçe duyarsız arama: İ/I/ı/i katlaması ve diyakritik sorunlarını çözer
    const term = normalizeTr(val);
    setFilteredServices(services.filter(s => normalizeTr(s.name).includes(term)));
  };

  // ── Capitalize ───────────────────────────────────────────────────────────────
  const capitalizeFirstLetter = (str) => {
    if (!str) return str;
    const turkishMap = { 'i': 'İ', 'ı': 'I' };
    return str.trim()
      .replace(/\(([^)]+)\)/g, ' ($1) ')
      .split(/\s+/)
      .filter(w => w.length > 0)
      .map(word => {
        if (word.startsWith('(') && word.endsWith(')')) {
          const inner = word.slice(1, -1);
          return `(${inner.charAt(0).toUpperCase() + inner.slice(1).toLowerCase()})`;
        }
        const first = word.charAt(0);
        return (turkishMap[first.toLowerCase()] || first.toUpperCase()) + word.slice(1).toLowerCase();
      })
      .join(' ');
  };

  // ── Modal ────────────────────────────────────────────────────────────────────
  const openModal = (item = null, col = 'services') => {
    setEditingCollection(col);
    setSelectedPkgServiceId('');
    setSaving(false);

    if (item) {
      setIsEditing(true);
      setSelectedItemId(item.id);
      if (col === 'packages') {
        setServiceForm({
          name: item.name || '',
          price: String(item.price ?? ''),
          type: 'Paket',
          status: item.status || 'active',
          description: item.description || '',
          packageServices: item.services ? [...item.services] : [],
          validityDays: item.validityDays ?? 0,
        });
      } else {
        setServiceForm({
          name: item.name || '',
          price: String(item.price ?? ''),
          type: item.type || 'İşlem',
          status: item.status || 'active',
          description: '',
          packageServices: [],
          validityDays: 0,
          trackStock: item.trackStock ?? true,
          stockQty: item.stockQty ?? 0,
          minStockQty: item.minStockQty ?? 5,
          unit: item.unit || 'adet',
        });
      }
    } else {
      setIsEditing(false);
      setSelectedItemId(null);
      setServiceForm(emptyForm());
    }
    setModalIsOpen(true);
  };

  const closeModal = () => {
    setModalIsOpen(false);
    setSelectedPkgServiceId('');
  };

  // ── Package service rows ─────────────────────────────────────────────────────
  const addPkgServiceRow = () => {
    if (!selectedPkgServiceId) return;
    const svc = services.find(s => s.id === selectedPkgServiceId);
    if (!svc) return;
    if (serviceForm.packageServices.find(s => s.serviceId === selectedPkgServiceId)) {
      alert('Bu hizmet zaten eklendi');
      return;
    }
    setServiceForm(p => ({
      ...p,
      packageServices: [...p.packageServices, { serviceId: svc.id, serviceName: svc.name, sessions: 1 }],
    }));
    setSelectedPkgServiceId('');
  };

  const updatePkgSessions = (serviceId, val) => {
    const n = Math.max(1, parseInt(val) || 1);
    setServiceForm(p => ({
      ...p,
      packageServices: p.packageServices.map(s => s.serviceId === serviceId ? { ...s, sessions: n } : s),
    }));
  };

  const removePkgServiceRow = (serviceId) => {
    setServiceForm(p => ({
      ...p,
      packageServices: p.packageServices.filter(s => s.serviceId !== serviceId),
    }));
  };

  // ── Save ─────────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (serviceForm.type === 'Paket') {
      await handleSavePackage();
    } else {
      await handleSaveService();
    }
  };

  const handleSaveService = async () => {
    try {
      const formattedName = capitalizeFirstLetter(serviceForm.name);
      const servicesRef = collection(db, "services");

      // Duplicate check
      const dupSnap = await getDocs(query(
        servicesRef,
        where("businessId", "==", user.businessId),
        where("name", "==", formattedName),
        where("status", "==", "active"),
      ));
      if (dupSnap.docs.some(d => d.id !== selectedItemId)) {
        alert(t('services.duplicateAlert'));
        return;
      }

      const data = { ...serviceForm, name: formattedName };
      if (isEditing) {
        await updateDoc(doc(db, "services", selectedItemId), data);
        alert(t('services.savedAlert'));
      } else {
        await addDoc(servicesRef, { ...data, businessId: user.businessId });
        alert(t('services.addedAlert'));
      }
      fetchServices();
      closeModal();
    } catch (err) {
      console.error("İşlem kaydedilirken hata:", err);
    }
  };

  const handleSavePackage = async () => {
    if (!serviceForm.name.trim()) { alert('Paket adı gerekli'); return; }
    if (!serviceForm.price || isNaN(Number(serviceForm.price))) { alert('Geçerli bir fiyat girin'); return; }
    if (serviceForm.packageServices.length === 0) { alert('En az bir hizmet ekleyin'); return; }

    setSaving(true);
    try {
      const data = {
        businessId: user.businessId,
        name: capitalizeFirstLetter(serviceForm.name.trim()),
        description: (serviceForm.description || '').trim(),
        price: Number(serviceForm.price),
        validityDays: Number(serviceForm.validityDays) || 0,
        services: serviceForm.packageServices,
        updatedAt: serverTimestamp(),
      };

      if (isEditing) {
        await updateDoc(doc(db, 'packages', selectedItemId), data);
        alert('Paket güncellendi');
      } else {
        data.status = 'active';
        data.createdAt = serverTimestamp();
        await addDoc(collection(db, 'packages'), data);
        alert('Paket eklendi');
      }
      fetchPackages();
      closeModal();
    } catch (err) {
      alert('Kayıt hatası: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // ── Delete / toggle ──────────────────────────────────────────────────────────
  const handleDeleteService = async (serviceId) => {
    try {
      await updateDoc(doc(db, "services", serviceId), { status: "deleted" });
      alert(t('services.deletedAlert'));
      fetchServices();
    } catch (err) {
      console.error("Silme hatası:", err);
    }
  };

  const togglePackageStatus = async (pkg) => {
    const newStatus = pkg.status === 'active' ? 'inactive' : 'active';
    try {
      await updateDoc(doc(db, 'packages', pkg.id), { status: newStatus, updatedAt: serverTimestamp() });
      fetchPackages();
    } catch (err) {
      alert('Durum güncellenemedi: ' + err.message);
    }
  };

  // ── Stock store ──────────────────────────────────────────────────────────────
  const { openAdjust } = useStockStore();

  // Stok güncellendikten sonra local state'i de güncelle
  const handleStockDone = (serviceId, newQty) => {
    setServices(prev => prev.map(s => s.id === serviceId ? { ...s, stockQty: newQty } : s));
    setFilteredServices(prev => prev.map(s => s.id === serviceId ? { ...s, stockQty: newQty } : s));
  };

  // ── Derived ──────────────────────────────────────────────────────────────────
  const activePackages = packages.filter(p => p.status === 'active');
  const inactivePackages = packages.filter(p => p.status !== 'active');
  const isPackageModal = serviceForm.type === 'Paket';

  // Paket modalında seçilebilecek hizmetler (paket olmayanlar, zaten eklenmemiş olanlar)
  const selectableServices = services
    .filter(s => s.type !== 'Paket')
    .filter(s => !serviceForm.packageServices.find(ps => ps.serviceId === s.id));

  // ── JSX ──────────────────────────────────────────────────────────────────────
  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
        <div className="relative w-full md:w-96">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          <input
            type="text"
            placeholder={t('search.placeholder')}
            value={searchTerm}
            onChange={handleSearch}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <button
          onClick={() => openModal()}
          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-md hover:from-cyan-600 hover:to-blue-600 transition-all duration-300"
        >
          <PlusCircle className="h-5 w-5" />
          {t('services.newBtn')}
        </button>
      </div>

      {/* ── İşlemler ── */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-4 text-gray-800 flex items-center gap-2">
          <FileText className="h-5 w-5 text-blue-500" />
          {t('services.title')}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredServices
            .filter(s => s.type === 'İşlem' || !s.type)
            .map(service => (
              <ServiceCard
                key={service.id}
                service={service}
                onEdit={() => openModal(service, 'services')}
                onDelete={() => handleDeleteService(service.id)}
                icon={<FileText className="h-5 w-5 text-blue-500 mt-1 mr-2 flex-shrink-0" />}
                t={t}
              />
            ))}
        </div>
      </div>

      {/* ── Ürünler ── */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2 mb-4">
          <Package className="h-5 w-5 text-pink-500" />
          {t('services.products')}
        </h2>
        {!isStockAllowed ? (
          <UpgradeBanner
            icon={<Package className="h-5 w-5 text-pink-500" />}
            title="Stok Yönetimi"
            description="Ürün stoklarını takip edin, adisyon kaydında otomatik düşürün."
          />
        ) : (() => {
          const products = filteredServices.filter(s => s.type === 'Ürün');
          const lowStock = products.filter(s => s.trackStock && s.minStockQty > 0 && (s.stockQty ?? 0) <= s.minStockQty);
          return (
            <>
              {lowStock.length > 0 && (
                <div className="flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full w-fit mb-3">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  {lowStock.length} ürün kritik seviyede
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {products.map(service => (
                  <ServiceCard
                    key={service.id}
                    service={service}
                    onEdit={() => openModal(service, 'services')}
                    onDelete={() => handleDeleteService(service.id)}
                    onAdjustStock={() => openAdjust(service)}
                    icon={<Package className="h-5 w-5 text-pink-500 mt-1 mr-2 flex-shrink-0" />}
                    t={t}
                  />
                ))}
              </div>
            </>
          );
        })()}
      </div>

      {isStockAllowed && <StockAdjustModal onDone={handleStockDone} />}

      {/* ── Paketler ── */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2 mb-4">
          <Layers className="h-5 w-5 text-purple-500" />
          {t('packages.title') || 'Paketler'}
        </h2>

        {!isPackagesAllowed ? (
          <UpgradeBanner
            icon={<Layers className="h-5 w-5 text-purple-500" />}
            title="Paket & Seans Takibi"
            description="Çok seanslı paketler oluşturun, müşteri bazında kalan seans sayısını otomatik takip edin."
          />
        ) : packages.length === 0 ? (
          <div className="text-center py-10 bg-white rounded-xl border border-dashed border-gray-300">
            <Layers className="h-10 w-10 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500 font-medium">Henüz paket eklenmedi</p>
            <p className="text-xs text-gray-400 mt-1">"Yeni Ekle" butonundan hizmet tipi olarak Paket seçin</p>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              {activePackages.map(pkg => (
                <PackageCard
                  key={pkg.id}
                  pkg={pkg}
                  onEdit={() => openModal(pkg, 'packages')}
                  onToggle={() => togglePackageStatus(pkg)}
                />
              ))}
            </div>
            {inactivePackages.length > 0 && (
              <div className="mt-4 space-y-3">
                <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">Pasif Paketler</p>
                {inactivePackages.map(pkg => (
                  <PackageCard
                    key={pkg.id}
                    pkg={pkg}
                    onEdit={() => openModal(pkg, 'packages')}
                    onToggle={() => togglePackageStatus(pkg)}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Modal ── */}
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        style={{
          overlay: {
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          },
          content: {
            position: 'relative',
            top: 'auto', left: 'auto', right: 'auto', bottom: 'auto',
            maxWidth: isPackageModal ? '560px' : '500px',
            width: '90%',
            maxHeight: '90vh',
            margin: '0 auto',
            padding: 0,
            border: 'none',
            background: 'transparent',
            overflow: 'visible',
          },
        }}
      >
        <div className="bg-white rounded-lg shadow-xl flex flex-col max-h-[90vh]">
          {/* Header */}
          <div className="bg-gradient-to-r from-cyan-500 to-blue-500 p-4 flex justify-between items-center rounded-t-lg flex-shrink-0">
            <h2 className="text-xl font-semibold text-white flex items-center gap-2">
              {isPackageModal
                ? <Package className="h-5 w-5" />
                : <FileText className="h-5 w-5" />}
              {isEditing ? t('modal.titleEdit') : t('modal.titleNew')}
            </h2>
            <button onClick={closeModal} className="text-white hover:text-gray-200 transition-colors">
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Body */}
          <div className="p-6 space-y-4 overflow-y-auto flex-1">
            {/* Hizmet Tipi */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <Tag className="h-4 w-4 text-blue-500" />
                {t('modal.typeLabel')}
              </label>
              <select
                value={serviceForm.type}
                disabled={isEditing}
                onChange={e => setServiceForm(p => ({
                  ...emptyForm(),
                  type: e.target.value,
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
              >
                <option value="İşlem">{t('services.typeService')}</option>
                {isStockAllowed    && <option value="Ürün">{t('services.typeProduct')}</option>}
                {isPackagesAllowed && <option value="Paket">Paket</option>}
              </select>
              {isEditing && (
                <p className="text-xs text-gray-400">Hizmet tipi düzenleme modunda değiştirilemez.</p>
              )}
            </div>

            {/* İsim */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <FileText className="h-4 w-4 text-blue-500" />
                {t('modal.nameLabel')}
              </label>
              <input
                type="text"
                placeholder={t('modal.namePlaceholder')}
                value={serviceForm.name}
                onChange={e => setServiceForm(p => ({ ...p, name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Fiyat */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <DollarSign className="h-4 w-4 text-blue-500" />
                {t('modal.priceLabel')}
                {isPackageModal && (
                  <span className="text-xs text-gray-400 font-normal">(paket toplam fiyatı)</span>
                )}
              </label>
              <input
                type="number"
                min="0"
                placeholder={t('modal.pricePlaceholder')}
                value={serviceForm.price}
                onChange={e => setServiceForm(p => ({ ...p, price: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* ── ÜRÜN'E ÖZEL ALANLAR ── */}
            {serviceForm.type === 'Ürün' && (
              <div className="border border-pink-100 rounded-xl p-4 space-y-4 bg-pink-50/40">
                <p className="text-xs font-semibold text-pink-700 uppercase tracking-wider flex items-center gap-1.5">
                  <Layers className="h-3.5 w-3.5" />
                  Stok Yönetimi
                </p>

                {/* Stok takibi toggle */}
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-700">Stok takibi</p>
                    <p className="text-xs text-gray-400">Kapalıysa stok sayısı gösterilmez</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setServiceForm(p => ({ ...p, trackStock: !p.trackStock }))}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      serviceForm.trackStock ? 'bg-pink-500' : 'bg-gray-300'
                    }`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow ${
                      serviceForm.trackStock ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>

                {serviceForm.trackStock && (
                  <>
                    {/* Birim */}
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium text-gray-700">Birim</label>
                      <select
                        value={serviceForm.unit}
                        onChange={e => setServiceForm(p => ({ ...p, unit: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-400"
                      >
                        {['adet', 'ml', 'gr', 'lt', 'kg', 'kutu', 'şişe', 'paket', 'tüp'].map(u => (
                          <option key={u} value={u}>{u}</option>
                        ))}
                      </select>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      {/* Başlangıç stoku — sadece yeni eklemede */}
                      {!isEditing && (
                        <div className="space-y-1.5">
                          <label className="text-sm font-medium text-gray-700">
                            Başlangıç stoku
                          </label>
                          <input
                            type="number"
                            min="0"
                            value={serviceForm.stockQty}
                            onChange={e => setServiceForm(p => ({ ...p, stockQty: e.target.value }))}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-400"
                          />
                        </div>
                      )}

                      {/* Minimum stok eşiği */}
                      <div className={`space-y-1.5 ${!isEditing ? '' : 'col-span-2'}`}>
                        <label className="text-sm font-medium text-gray-700">
                          Min. stok eşiği
                          <span className="text-xs text-gray-400 font-normal ml-1">(uyarı için)</span>
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={serviceForm.minStockQty}
                          onChange={e => setServiceForm(p => ({ ...p, minStockQty: e.target.value }))}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pink-400"
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* ── PAKET'E ÖZEL ALANLAR ── */}
            {isPackageModal && (
              <>
                {/* Açıklama */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Açıklama
                    <span className="text-xs text-gray-400 font-normal ml-1">(opsiyonel)</span>
                  </label>
                  <input
                    type="text"
                    value={serviceForm.description}
                    onChange={e => setServiceForm(p => ({ ...p, description: e.target.value }))}
                    placeholder="Ör: 10 seans lazer epilasyon paketi"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Geçerlilik */}
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <Clock className="h-4 w-4 text-blue-500" />
                    Geçerlilik süresi (gün)
                    <span className="text-xs text-gray-400 font-normal">0 = süresiz</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={serviceForm.validityDays}
                    onChange={e => setServiceForm(p => ({ ...p, validityDays: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* İçerilen Hizmetler */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    İçerilen Hizmetler
                  </label>
                  <div className="flex gap-2">
                    <select
                      value={selectedPkgServiceId}
                      onChange={e => setSelectedPkgServiceId(e.target.value)}
                      className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Hizmet seçin...</option>
                      {selectableServices.map(s => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={addPkgServiceRow}
                      disabled={!selectedPkgServiceId}
                      className="flex items-center gap-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-200 disabled:text-gray-400 text-white text-sm font-medium px-3 py-2 rounded-lg transition-colors"
                    >
                      <Plus className="h-4 w-4" />
                      Ekle
                    </button>
                  </div>

                  {serviceForm.packageServices.length > 0 && (
                    <div className="mt-2 space-y-2">
                      {serviceForm.packageServices.map(svc => (
                        <div key={svc.serviceId} className="flex items-center gap-3 bg-gray-50 rounded-lg px-3 py-2">
                          <span className="flex-1 text-sm text-gray-700">{svc.serviceName}</span>
                          <div className="flex items-center gap-1.5">
                            <label className="text-xs text-gray-500">Seans</label>
                            <input
                              type="number"
                              min="1"
                              value={svc.sessions}
                              onChange={e => updatePkgSessions(svc.serviceId, e.target.value)}
                              className="w-14 border border-gray-300 rounded px-2 py-1 text-sm text-center focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => removePkgServiceRow(svc.serviceId)}
                            className="text-red-400 hover:text-red-600 p-1"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {serviceForm.packageServices.length === 0 && (
                    <p className="text-xs text-gray-400 text-center py-2">
                      Pakete en az bir hizmet ekleyin
                    </p>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3 rounded-b-lg flex-shrink-0">
            <button
              onClick={closeModal}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors"
            >
              {t('modal.cancelBtn')}
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 rounded-md transition-all disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <Save className="h-4 w-4" />
              {isEditing ? t('modal.updateBtn') : t('modal.saveBtn')}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ── Service Card ──────────────────────────────────────────────────────────────
function ServiceCard({ service, onEdit, onDelete, onAdjustStock, icon, t }) {
  const isProduct = service.type === 'Ürün';
  const showStock = isProduct && service.trackStock;

  return (
    <div className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-start flex-1 min-w-0">
            {icon}
            <h3 className="font-medium text-gray-900 leading-tight">{service.name}</h3>
          </div>
          {showStock && (
            <StockBadge
              stockQty={service.stockQty}
              minStockQty={service.minStockQty}
              unit={service.unit}
            />
          )}
        </div>
        <div className="flex items-center mb-4">
          <Tag className="h-5 w-5 text-green-500 mr-2" />
          <span className="text-lg font-semibold text-green-600">{service.price} TL</span>
        </div>
        {showStock && (
          <div className="flex items-center justify-between mb-4 bg-gray-50 rounded-lg px-3 py-2">
            <span className="text-xs text-gray-500">
              Stok: <span className="font-semibold text-gray-800">{service.stockQty ?? 0} {service.unit || 'adet'}</span>
            </span>
            {service.minStockQty > 0 && (
              <span className="text-xs text-gray-400">
                Eşik: {service.minStockQty}
              </span>
            )}
          </div>
        )}
        <div className="flex justify-end gap-1">
          {isProduct && service.trackStock && onAdjustStock && (
            <button
              onClick={onAdjustStock}
              className="inline-flex items-center px-2 py-1.5 text-sm text-purple-600 hover:text-purple-700 hover:bg-purple-50 rounded-md transition-colors"
            >
              <Layers className="h-4 w-4 mr-1" />
              Stok
            </button>
          )}
          <button
            onClick={onEdit}
            className="inline-flex items-center px-2 py-1.5 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors"
          >
            <Edit2 className="h-4 w-4 mr-1" />
            {t('services.editBtn')}
          </button>
          <CustomDeleteDialog onConfirm={onDelete} />
        </div>
      </div>
    </div>
  );
}

// ── Upgrade Banner ────────────────────────────────────────────────────────────
function UpgradeBanner({ title, description }) {
  return (
    <div className="flex items-start gap-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-5">
      <div className="flex-shrink-0 mt-0.5">
        <AlertTriangle className="h-5 w-5 text-blue-500" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-blue-900">{title} — Profesyonel Plan Gerekli</p>
        <p className="text-xs text-blue-700 mt-0.5">{description}</p>
      </div>
      {IS_SAAS && (
        <Link
          to="/billing"
          className="flex-shrink-0 inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded-lg text-white bg-blue-600 hover:bg-blue-700 transition-colors"
        >
          Planı Yükselt
        </Link>
      )}
    </div>
  );
}

export default Services;
